import * as yup from 'yup';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Slide } from 'react-awesome-reveal';
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import constant from '../../../../utils/constants';
import { convertEpochToDateandTime } from '../../../../utils/commonFunctions';
import { useAppSelector } from '../../../../app/redux/hooks';
import CustomButton from '../../../../common/components/buttons/CustomButton';
import GoogleMap from '../../../../common/components/maps/googlemap/GoogleMap';
import GoogleMarkers from '../../../../common/components/maps/googlemarkers/GoogleMarkers';
import CustomDateTimeCalendar from '../../../../common/components/customized/customdatetimecalendar/CustomDateTimeCalendar';
import LoginRedirect from '../../../../common/components/loginredirect/LoginRedirect';
import { NotificationIcons } from './componentDatas/notificationIcons';
import { useNotificationFunctionHooks } from './hooks/notificationHooks';
import { useNotificationEffectHooks } from './hooks/useNotificationEffectHooks';
import { useNotificationCalenderHooks } from './hooks/notificationCalenderHooks';
import { dashboardVehiclesStatusAction } from '../../redux/reducer/yaantracSlices/dashboardSlice';
import {
  activeDatas,
  clearNotification,
  getNotificationHistory
} from '../../../../common/redux/reducer/commonSlices/notificationSlice';
import {
  map,
  updateCenter,
  updateZoom
} from '../../../../common/redux/reducer/commonSlices/mapSlice';
import './Notifications.scss';

interface Props {
  isOpen: string;
}

const notificationSchema: any = yup.object().shape({
  startDate: yup
    .date()
    .required('Start Date is required')
    .typeError('Start Date is invalid'),
  endDate: yup.date().required('End Date is required').typeError('End Date is invalid')
});

const Notifications = ({ isOpen }: Props) => {
  const { isLoading, data } = useAppSelector(state => state.notificationHistory);
  const notification = useAppSelector(state => state.notificationAutoplanner);
  const { markers } = useAppSelector((state: any) => state.map);
  const allVehicleData = useAppSelector(state => state.dashboardVehiclesStatus?.data);

  const urls = {
    getNotification: getNotificationHistory,
    clearNotification: clearNotification,
    getMapDetails: dashboardVehiclesStatusAction,
    center: updateCenter,
    zoom: updateZoom,
    selecedDatas: activeDatas,
    map: map
  };

  const { Events, IconTypes } = NotificationIcons();

  const [typeList, setTypeListEvent] = useState<any>('All');
  const [count, setCount] = useState<number>(6);
  const [getStartDate, setGetStartDate] = useState<Date | null>(null);
  const [getEndDate, setGetEndDate] = useState<Date | null>(null);
  const [sidebarOpen, setSideBarOpen] = useState<any>(false);
  const [datas, setDatas] = useState<any>(data);

  const themes = useTheme();
  const isXs = useMediaQuery(themes.breakpoints.up('sm'));

  const { control, handleSubmit, setValue, clearErrors, reset } = useForm({
    resolver: yupResolver(notificationSchema)
  });

  const {
    handleSubmitNotification,
    sideBarHandleCLick,
    handleViewOpen,
    handleClickEvent,
    handleLoadMore,
    events,
    typeLoad,
    view
  } = useNotificationFunctionHooks({
    Events,
    allVehicleData,
    count,
    setCount,
    typeList,
    setGetStartDate,
    setGetEndDate,
    getStartDate,
    getEndDate,
    reset,
    urls,
    setTypeListEvent,
    isXs,
    setSideBarOpen,
    sidebarOpen,
    datas,
    setDatas
  });

  const {
    handleToDate,
    handleCustomTo,
    handleCustomFrom,
    handleFromDate,
    toDateDisabled
  } = useNotificationCalenderHooks({
    setValue,
    clearErrors,
    setGetStartDate,
    setGetEndDate,
    getStartDate
  });

  useNotificationEffectHooks({
    data,
    notification,
    allVehicleData,
    isOpen,
    getStartDate,
    getEndDate,
    count,
    typeList,
    urls,
    isXs,
    setSideBarOpen,
    setDatas
  });

  return (
    <>
      <Box className='notifiction-title'>
        <Typography paragraph className='notification-head'>
          {constant.Notification}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            cursor: 'pointer',
            zIndex: 1,
            alignItems: 'center'
          }}
          component='form'
          onSubmit={handleSubmit(handleSubmitNotification)}
        >
          <CustomDateTimeCalendar
            id='select-startdate'
            name='startDate'
            control={control}
            disableFuture={true}
            disablePast={false}
            value={getStartDate}
            format='DD/MM/YYYY hh:mm a'
            placeholder='Start Date'
            getStartDate={getStartDate}
            isDisabled={false}
            className='reports-calendar animate__animated animate__slideInRight'
            onDateSelect={(start: Date, end: Date) => handleCustomFrom(start, end)}
            onDateChange={(date: Date) => handleFromDate(date)}
            type='report'
          />

          <CustomDateTimeCalendar
            id='select-enddate'
            name='endDate'
            control={control}
            placeholder='End Date'
            format='DD/MM/YYYY hh:mm a'
            disableFuture={true}
            disablePast={false}
            className='reports-calendar animate__animated animate__slideInRight'
            getStartDate={getStartDate}
            value={getEndDate}
            onDateSelect={(start: Date, end: Date) => handleCustomTo(start, end)}
            onDateChange={(date: Date) => handleToDate(date)}
            minDateTime={
              getStartDate !== null ? dayjs(getStartDate?.getTime() + 1000 * 600) : null
            }
            maxDateTime={dayjs()
              .set('hour', new Date().getHours())
              .set('minutes', new Date().getMinutes())
              .startOf('minute')}
            isDisabled={toDateDisabled}
            type='report'
          />
          <Box className='Search-btn'>
            <CustomButton type='submit' className='saveChanges' category='Search' />
          </Box>
        </Box>
      </Box>

      <Box
        className='notification-component'
        sx={{
          height: '650px',
          padding: '5px',
          display: 'flex'
        }}
      >
        <Grid
          item
          sx={{
            display: {
              xs: 'block',
              sm: 'none'
            }
          }}
        >
          <Icon
            icon='fe:bar'
            style={{ color: 'black', top: '50px', left: '75px', position: 'absolute' }}
            height={10}
            width={10}
            onClick={() => sideBarHandleCLick()}
          />
        </Grid>
        {sidebarOpen && (
          <Box
            sx={{
              width: '220px',
              marginRight: '15px',
              backgroundColor: { sm: '#897b7b0a', xs: 'white' },
              padding: '15px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              height: '82vh',
              overflowY: 'scroll',
              zIndex: '1'
            }}
          >
            {events.map((data: any, index: number) => (
              <Slide direction='left' triggerOnce>
                <Box
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '13px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleClickEvent(data, index)}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animationDuration: '0.6s'
                  }}
                  className={data.active ? 'active-color ' : 'non-active '}
                >
                  <Box
                    key={index}
                    sx={{
                      marginRight: '15px',
                      borderRadius: '40px'
                    }}
                  >
                    {data.Icon}
                  </Box>
                  <Typography> {data?.eventName}</Typography>
                </Box>
              </Slide>
            ))}
          </Box>
        )}
        {datas?.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '40%'
            }}
          >
            <Box
              sx={{
                marginRight: '5px',
                overflowY: 'scroll',
                zIndex: '0',
                overflowX: 'hidden',
                height: '80vh'
              }}
            >
              {datas?.length > 0 &&
                datas?.map((item: any, index: number) => (
                  <Box
                    key={index}
                    className={
                      item?.active
                        ? 'active-color notification-card animate__animated animate__slideInRight animate__faster  '
                        : 'non-active notification-card animate__animated animate__slideInRight animate__faster'
                    }
                    style={{
                      animationDelay: `${(index % 6) * 0.1}s`,
                      animationDuration: '0.8s'
                    }}
                    onClick={() => handleViewOpen(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      border: '1px solid #ddd',
                      gap: '10px',
                      borderRadius: '8px',
                      overflowY: 'scroll',
                      marginBottom: '10px',
                      transition: 'background-color 0.2s',
                      overFlowX: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        marginRight: '15px',
                        borderRadius: '30px',
                        marginBottom: '8px'
                      }}
                    >
                      {Object.keys(IconTypes).includes(item?.event)
                        ? IconTypes[item?.event]
                        : IconTypes['others']}
                    </Box>
                    <Box className='message-content'>
                      <Typography
                        className='heading'
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '16px',
                          marginBottom: '5px',
                          color: 'white'
                        }}
                      >
                        {item?.event}
                      </Typography>
                      <Typography
                        className='content'
                        sx={{
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '5px'
                        }}
                      >
                        {item?.message.replace(
                          item?.vehicleNumber,
                          item?.vehicleNumber?.toUpperCase()
                        )}
                      </Typography>
                      <Typography
                        className='data-time'
                        sx={{
                          fontSize: '12px',
                          color: '#999'
                        }}
                      >
                        {convertEpochToDateandTime(item?.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Box>
            <Box>
              {datas?.length >= 5 && (
                <Box className='loadmore-btn'>
                  <CustomButton
                    onClick={() => handleLoadMore()}
                    loading={datas?.length !== 0 && isLoading && typeLoad === 'Load More'}
                    className='saveChanges'
                    category='Load More'
                  />
                </Box>
              )}
            </Box>
          </Box>
        ) : isLoading && datas?.length === 0 ? (
          <Box sx={{ margin: '0 0 15px 0', width: '80%' }}>
            {[...Array(4)].map((_, index) => (
              <Box className='loader-box' key={index}>
                <Skeleton variant='text' sx={{ fontSize: '25px', marginBottom: '4px' }} />
                <Skeleton variant='text' sx={{ fontSize: '1rem' }} />
                <Skeleton variant='text' className='time-loader' />
              </Box>
            ))}
          </Box>
        ) : (
          <Typography className='no-notification-text'>No Notification Found!</Typography>
        )}

        {datas?.length > 0 &&
          (view && typeLoad === 'map-view' && markers?.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '40%',
                height: '82vh',
                border: '2px solid #DED7DA',
                borderRadius: '4px'
              }}
            >
              <GoogleMap children={<GoogleMarkers />} />
            </Box>
          ) : (
            <Typography className='map-view'>No Location found!</Typography>
          ))}
      </Box>
      <LoginRedirect />
    </>
  );
};

export default Notifications;
